(() => {
  if (customElements.get('conlemany-home-video')) return;

  const preferenceKey = 'conlemany:homepage-motion:v1';
  const components = new Set();
  let reduceMotion = false;
  try {
    reduceMotion = window.localStorage.getItem(preferenceKey) === 'reduced';
  } catch {}

  function motionEnabled() {
    return !reduceMotion;
  }

  function refresh() {
    components.forEach((component) => component.updateMotion());
  }

  window.addEventListener('storage', (event) => {
    if (event.key !== preferenceKey && event.key !== null) return;
    reduceMotion = event.newValue === 'reduced';
    refresh();
  });
  document.addEventListener('visibilitychange', refresh);
  document.addEventListener('pointerdown', refresh, { passive: true });
  document.addEventListener('keydown', refresh);
  window.addEventListener('pageshow', refresh);

  class HomeVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.abort = new AbortController();
      this.inView = false;
      this.pending = false;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.loop = true;
      components.add(this);
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting && entry.intersectionRatio > 0;
        this.updateMotion();
      }, { threshold: [0, 0.05] });
      this.observer.observe(this);
      this.video.addEventListener('play', () => {
        if (!this.shouldPlay()) this.video.pause();
      }, { signal: this.abort.signal });
    }

    disconnectedCallback() {
      components.delete(this);
      this.observer?.disconnect();
      this.abort?.abort();
      this.video?.pause();
    }

    shouldPlay() {
      return this.isConnected && motionEnabled() && this.inView && !document.hidden;
    }

    updateMotion() {
      if (!this.shouldPlay()) {
        this.video.pause();
        return;
      }
      if (!this.video.paused) return;
      if (this.pending) {
        this.retryAfterPending = true;
        return;
      }
      this.video.muted = true;
      this.retryAfterPending = false;
      this.pending = true;
      this.video.play().then(() => {
        if (!this.shouldPlay()) this.video.pause();
      }).catch(() => {}).finally(() => {
        this.pending = false;
        if (this.retryAfterPending && this.shouldPlay() && this.video.paused) this.updateMotion();
      });
    }
  }

  class HomeMarquee extends HTMLElement {
    connectedCallback() {
      this.inner = this.querySelector('.marquee__inner');
      this.viewport = this.querySelector('.conlemany-motion__viewport');
      this.original = this.inner?.querySelector('.marquee__items');
      if (!this.original) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.inView = false;
      this.hovered = false;
      this.focused = false;
      this.original.classList.add('animate');
      this.style.setProperty('--duration', `${Number(this.dataset.duration) || 20}s`);
      components.add(this);
      this.viewport.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' && this.dataset.pauseOnHover === 'true') {
          this.hovered = true;
          this.updateMotion();
        }
      }, options);
      this.viewport.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.updateMotion();
      }, options);
      this.viewport.addEventListener('focusin', () => {
        this.focused = true;
        this.updateMotion();
      }, options);
      this.viewport.addEventListener('focusout', () => {
        queueMicrotask(() => {
          this.focused = this.viewport.contains(document.activeElement);
          this.updateMotion();
        });
      }, options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.updateMotion();
      });
      this.observer.observe(this);
      this.resize = new ResizeObserver(() => this.fillTrack());
      this.resize.observe(this.viewport);
      this.resize.observe(this.original);
      this.fillTrack();
      this.updateMotion();
    }

    disconnectedCallback() {
      components.delete(this);
      this.abort?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
    }

    fillTrack() {
      const width = this.original.getBoundingClientRect().width;
      if (!width) return;
      const count = Math.min(20, Math.ceil(this.viewport.clientWidth / width) + 1);
      const existing = this.inner.querySelectorAll('[data-motion-clone]');
      if (existing.length === count) return;
      existing.forEach((clone) => clone.remove());
      for (let index = 0; index < count; index += 1) {
        const clone = this.original.cloneNode(true);
        clone.dataset.motionClone = '';
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });
        this.inner.append(clone);
      }
    }

    updateMotion() {
      this.dataset.static = String(!motionEnabled() || this.focused);
      this.dataset.paused = String(!motionEnabled() || document.hidden || !this.inView || this.hovered || this.focused);
    }
  }

  class HomeMotionPreference extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.abort = new AbortController();
      components.add(this);
      this.hidden = false;
      this.button.addEventListener('click', () => {
        reduceMotion = !reduceMotion;
        try {
          window.localStorage.setItem(preferenceKey, reduceMotion ? 'reduced' : 'full');
        } catch {}
        refresh();
      }, { signal: this.abort.signal });
      this.updateMotion();
    }

    disconnectedCallback() {
      components.delete(this);
      this.abort?.abort();
    }

    updateMotion() {
      this.button.setAttribute('aria-checked', String(reduceMotion));
      this.dataset.reduced = String(reduceMotion);
    }
  }

  customElements.define('conlemany-home-video', HomeVideo);
  customElements.define('conlemany-home-marquee', HomeMarquee);
  customElements.define('conlemany-home-motion-preference', HomeMotionPreference);
})();
